/**
 * This service is responsible for all communication with third party social media marketing pixels and is expected to enforce
 * our privacy policies. The implementation should be made in such a way that consumers of this service does not risk
 * abusing these policies (unless deliberately trying to).
 *
 * If you are adding new events, or adding data payloads to existing events, make sure that you have read and understood
 * our provacy policies first (https://www.naturalcycles.com/other/legal/privacy).
 *
 * All public methods are enforced to be tested as tracking events unless explicitly excluded.
 * */
import { _isTruthy, _objectEntries, pMap } from '@naturalcycles/js-lib';
import { ProductType } from '../../model/product.shared.model';
import { browserCrypto } from '../browserCrypto.service';
import { pixelClients } from './socialMediaMarketing.clients';
import { CookieBotConsentType } from './socialMediaMarketing.model';
import { allowedDomains, debugDomainMap, domainPathKeywordSuppressionMap, } from './socialMediaMarketing.privacySettings';
export class SocialMediaMarketingService {
    constructor(cfg = { debug: false }) {
        this.cfg = cfg;
        this.isInitialized = false;
    }
    /**
     * @description Loads the pixels that are allowed in the current context (consent settings and URL).
     */
    init() {
        if (this.isInitialized) {
            return;
        }
        const allowedPixels = this.getAllowedPixels();
        if (!allowedPixels.length) {
            this.log('No pixel clients allowed to load');
            return;
        }
        this.loadPixels(allowedPixels);
        this.isInitialized = true;
    }
    loadPixels(pixels) {
        for (const { client } of pixels) {
            client.load();
        }
        this.log(`Pixel clients loaded`);
    }
    pageView({ id }) {
        this.init();
        const allowedPixels = this.getAllowedPixels();
        if (!allowedPixels.length) {
            this.log('🔴 PageView blocked');
            return;
        }
        this.log('📜 PageView');
        for (const { client } of allowedPixels) {
            client.pageView({ id });
        }
    }
    async lead({ id, email }) {
        this.init();
        const allowedPixels = this.getAllowedPixels();
        if (!allowedPixels.length) {
            this.log('🔴 Lead blocked');
            return;
        }
        this.log('📝 Lead');
        const [hashedEmail] = await this.obfuscateMany([email]);
        for (const { client } of allowedPixels) {
            client.lead({ id, hashedEmail });
        }
    }
    addToCart({ id, amount, currency, products }) {
        this.init();
        const allowedPixels = this.getAllowedPixels();
        if (!allowedPixels.length) {
            this.log('🔴 Add to cart blocked');
            return;
        }
        this.log('🛒 Add to cart');
        const plan = this.getPlanFromProducts(products);
        for (const { client } of allowedPixels) {
            client.addToCart({ id, amount, currency, plan });
        }
    }
    async purchase({ id, amount, currency, products, email, phone, }) {
        this.init();
        const allowedPixels = this.getAllowedPixels();
        if (!allowedPixels.length) {
            this.log('🔴 Purchase blocked');
            return;
        }
        this.log('💳 Purchase');
        const plan = this.getPlanFromProducts(products);
        const [hashedEmail, hashedPhone] = await this.obfuscateMany([email, phone]);
        for (const { client } of allowedPixels) {
            client.purchase({ id, amount, currency, plan, hashedEmail, hashedPhone });
        }
    }
    postPurchase({ id }) {
        this.init();
        const allowedPixels = this.getAllowedPixels();
        if (!allowedPixels.length) {
            this.log('🔴 Post Purchase blocked');
            return;
        }
        this.log('🧾 Post Purchase');
        for (const { client } of allowedPixels) {
            client.postPurchase?.({ id });
        }
    }
    /** We can only send the plan that the user chose, other products are considered sensitive */
    getPlanFromProducts(products) {
        return products.find(p => p?.type === ProductType.SUBSCRIPTION)?.key;
    }
    /** Obfuscates array of strings with common social media marketing best practices (trim - lowercase - sha256) */
    async obfuscateMany(messages) {
        const filteredMessages = messages.filter(_isTruthy);
        if (!filteredMessages.length) {
            return [];
        }
        return await pMap(filteredMessages, message => browserCrypto.sha256(message.trim().toLowerCase()));
    }
    getCurrentHref() {
        return window.location.href;
    }
    getCookieBotConsent() {
        return globalThis.window.Cookiebot?.consent;
    }
    getAllowedPixels() {
        const href = this.getCurrentHref();
        const domain = this.getDomainFromURL(href);
        // Domain check is necessary for all pixels
        if (!domain) {
            this.log('🔴 Current domain not allowed to send any events.');
            return [];
        }
        let allowedPixels = Object.values(pixelClients);
        // Path check is only relevant for marketing pixels
        if (this.isURLPathSuppressed(domain, href)) {
            this.log('🔴 Current url path does not allow marketing pixels.');
            allowedPixels = allowedPixels.filter(client => client.requiredConsent === CookieBotConsentType.statistics);
        }
        const consent = this.getCookieBotConsent();
        this.log(`User consent settings: `);
        this.log(`Marketing: ${consent?.marketing} Statistics: ${consent?.statistics}`);
        allowedPixels = allowedPixels.filter(client => consent?.[client.requiredConsent]);
        return allowedPixels;
    }
    getDomainFromURL(url) {
        let domain = allowedDomains.find(d => url.startsWith(d));
        if (!domain && this.cfg.debug) {
            _objectEntries(debugDomainMap).forEach(([localHost, productionDomain]) => {
                if (url.startsWith(localHost)) {
                    domain = productionDomain;
                }
            });
        }
        return domain;
    }
    isURLPathSuppressed(domain, url) {
        return domainPathKeywordSuppressionMap[domain].some(d => url.includes(d));
    }
    log(message) {
        if (!this.cfg.debug) {
            return;
        }
        console.log(`[SocialMediaMarketingService] ${this.cfg.debug ? '[DEBUG] ' : ''}${message}`);
    }
}
